// Top-level backend bootstrap. Call once at app start (BootScene.create).
//   - Init Supabase auth & subscribe to session changes
//   - On sign-in, install CloudSaveStore (preloaded) and call save.load()
//   - On sign-out, fall back to LocalSaveStore
//
// Safe to call when backend isn't configured: everything no-ops, the game
// runs purely from localStorage exactly like before.

import { isBackendConfigured } from "./client";
import { initAuth, onAuthChange, getSession } from "./auth";
import { CloudSaveStore } from "./CloudSaveStore";
import { save, setSaveStore, useLocalSaveStore } from "../systems/Save";

let cloudStore: CloudSaveStore | null = null;
let bootstrapped = false;

/** A hanging auth request must never stall the boot splash forever. */
const AUTH_TIMEOUT_MS = 6000;

export async function initBackend(): Promise<void> {
  if (!isBackendConfigured()) {
    save.load(); // local-only path
    return;
  }

  // Race auth+session bootstrap against a timeout. On timeout, boot with the
  // local save so the menu appears; if auth completes later anyway, the
  // still-chained applySessionState (and onAuthChange) install the cloud
  // store and reload from it.
  const timedOut = await Promise.race([
    initAuth().then(() => applySessionState()).then(() => false),
    new Promise<boolean>(res => setTimeout(() => res(true), AUTH_TIMEOUT_MS)),
  ]);
  if (timedOut && !bootstrapped) {
    useLocalSaveStore();
    save.load();
    bootstrapped = true;
  }

  onAuthChange(() => { void applySessionState(); });

  // Best-effort flush before tab close.
  window.addEventListener("pagehide", () => { void cloudStore?.flushNow(); });
}

async function applySessionState(): Promise<void> {
  const session = getSession();
  if (session) {
    // Signed in → install a fresh CloudSaveStore and (re)pull cloud data.
    cloudStore = new CloudSaveStore();
    await cloudStore.preload();
    setSaveStore(cloudStore);
    // First run uses load() so a guest's local progress is preserved if the
    // cloud is empty; subsequent logins force a reload from the cloud store.
    if (!bootstrapped) {
      save.load();
      bootstrapped = true;
    } else {
      save.reload();
    }
  } else {
    // Signed out → fall back to the local mirror and reload from it.
    cloudStore = null;
    useLocalSaveStore();
    if (!bootstrapped) {
      save.load();
      bootstrapped = true;
    } else {
      save.reload();
    }
  }
}
